import * as React from 'react';
import { InjectedRouter } from 'react-router';
import pluralize from 'pluralize';
import qs from 'qs';

import AdminDistrictSisDashboardApiHeader from 'admin/rostering/components/AdminDistrictSisDashboardApiHeader';
import AdminDistrictSisDashboardSftpHeader from 'admin/rostering/components/AdminDistrictSisDashboardSftpHeader';
import AdminSisSyncSchoolTable from 'admin/rostering/components/AdminSisSyncSchoolTable';
import CsvValidationTableContainer from 'admin/rostering/containers/CsvValidationTableContainer';
import Checkbox from 'components/Checkbox';
import Container from 'components/Container';
import CurrentUser from 'auth/CurrentUser';
import Link from 'components/Link';
import LinkConstants from 'constants/LinkConstants';
import Row from 'components/Row';
import Text from 'components/Text';
import Time from 'components/Time';

import {
  syncSetupConstants,
} from 'syncSetup/lib/syncSetupData';
import { getApiSyncType, getApiEventConfigurations } from 'syncSetup/lib/syncSetupData';
import {
  syncMethodfromType,
} from 'constants/SyncTypePickerConstants';
import {
  AdminDistrictSisDashboard_organizations_childOrganizations as ChildOrganization,
  AdminDistrictSisDashboard_organizations_sisSyncOverview_districtStatus as DistrictStatus,
} from 'admin/rostering/queries/__generated__/AdminDistrictSisDashboard';

import './styles/AdminDistrictSisDashboard.scss';

type Props = {
  orgUuid: string;
  districtStatus: DistrictStatus;
  childOrganizations: ChildOrganization[];
  rosteringPermissions: {
    canOnlySeeReviewScreen: boolean;
    canOnlySeeRosteringDashboard: boolean;
    canOnlySeeRosteringStagingArea: boolean;
  };
  router: InjectedRouter;
  canRosteredUsersLeaveOrg: boolean;
  handleRosteringEnforcementChange: (allowedToLeave: boolean) => void;
  handleSyncRequest: (orgUuid: string) => void;
};

export default class AdminDistrictSisDashboard extends React.Component<Props> {
  renderHeader = () => {
    const { childOrganizations, districtStatus, orgUuid, rosteringPermissions } = this.props;
    const { canOnlySeeReviewScreen, canOnlySeeRosteringStagingArea } = rosteringPermissions;

    if (districtStatus.syncType.toString().includes('Spreadsheet')) {
      return (
        <AdminDistrictSisDashboardSftpHeader
          orgUuid={orgUuid}
          districtStatus={districtStatus}
          childOrganizations={childOrganizations}
          canOnlySeeReviewScreen={canOnlySeeReviewScreen}
          canOnlySeeRosteringStagingArea={canOnlySeeRosteringStagingArea}
        />
      );
    } else {
      return (
        <AdminDistrictSisDashboardApiHeader
          orgUuid={orgUuid}
          districtStatus={districtStatus}
          childOrganizations={childOrganizations}
          canOnlySeeReviewScreen={canOnlySeeReviewScreen}
          canOnlySeeRosteringStagingArea={canOnlySeeRosteringStagingArea}
        />
      );
    }
  };

  renderLastUploadInfo = () => {
    const { districtStatus } = this.props;
    const { lastUploadTimestamp } = districtStatus;

    if (!lastUploadTimestamp) {
      return {
        date: null,
        time: null,
      };
    }

    const date = <Time date={new Date(lastUploadTimestamp)} month="long" day="numeric" />;
    const time = (
      <Time date={new Date(lastUploadTimestamp)} hour12 hour="numeric" minute="2-digit" />
    );

    return {
      date,
      time,
    };
  };

  renderRosteringEnforcementCheckbox = () => {
    const { canRosteredUsersLeaveOrg, handleRosteringEnforcementChange } = this.props;

    return (
      <Row>
        <Container size="medium" align="left" flush>
          <Checkbox
            data-test="rostering-enforcement-checkbox"
            label="Enable rostering enforcement"
            labelNote={
              <Text color="graphite" weight="normal">
                This removes the option for SIS-linked users to opt out of organization-wide
                messages. <br />
                They can still leave individual classes within the organization.{' '}
                <Link href={LinkConstants.ROSTERING_ENFORCEMENT} target="_blank">
                  Learn more
                </Link>
              </Text>
            }
            checked={!canRosteredUsersLeaveOrg}
            onChange={() => handleRosteringEnforcementChange(!canRosteredUsersLeaveOrg)}
          />
        </Container>
      </Row>
    );
  };

  renderApiSyncInfo = () => {
    const { orgUuid, districtStatus, handleSyncRequest } = this.props;
    const { syncType } = districtStatus;
    const { date, time } = this.renderLastUploadInfo();
    const { requiresOauth } = syncSetupConstants[syncType];
    const queryPath = requiresOauth ? '/sync_setup' : '/sync_setup/auth_details';
    const queryParams = qs.stringify({
      district_uuid: orgUuid,
      sync_type: syncType,
    });

    const syncTypeLabel = getApiSyncType(syncType);
    const fetchDataLink = (
      <React.Fragment>
        {' | '}
        <Link onClick={() => handleSyncRequest(orgUuid)} weight="semibold">
          Check for new {syncTypeLabel} data
        </Link>
      </React.Fragment>
    );

    return (
      <React.Fragment>
        <Text>
          Last {syncTypeLabel} sync:{' '}
          {!date ? (
            <Text> -- </Text>
          ) : (
            <Text>
              <Text weight="semibold">
                {date}, {time}
              </Text>
              {fetchDataLink}
              {' | '}
              <Link href={LinkConstants.SIS_SYNC_HELP} target="_blank">
                Get help
              </Link>{' '}
            </Text>
          )}
        </Text>
        <Link
          weight="semibold"
          href={`${queryPath}?${queryParams}`}
          eventElementName={getApiEventConfigurations(syncType).startAPISetup}
          className="setup-sync-settings-button">
          {syncTypeLabel} setup
        </Link>
      </React.Fragment>
    );
  };

  renderSftpUploadInfo = () => {
    const { orgUuid, districtStatus } = this.props;
    const { filesZipUrl, lastUploadFiles } = districtStatus;
    const { date, time } = this.renderLastUploadInfo();

    return (
      <React.Fragment>
        <Text>
          Last Upload:{' '}
          {!date ? (
            <Text> -- </Text>
          ) : (
            <Text>
              <Text weight="semibold">
                {date}, {time}
              </Text>
              {' | '}
              <Link weight="semibold" href={filesZipUrl || '#'}>
                Download {pluralize('file', lastUploadFiles.length)}
              </Link>
              {' | '}
              <Link weight="semibold" href={LinkConstants.SIS_SYNC_HELP} target="_blank">
                Get help
              </Link>{' '}
            </Text>
          )}
        </Text>
        <Link
          weight="semibold"
          href={`/sftp_sync_setup/details?district_uuid=${orgUuid}`}
          className="setup-sync-settings-button">
          SFTP credentials
        </Link>
      </React.Fragment>
    );
  };

  renderUploadInfo = () => {
    const { districtStatus } = this.props;
    const { syncType } = districtStatus;
    const syncMethod = syncMethodfromType(syncType);

    switch (syncMethod) {
      case 'API':
        return this.renderApiSyncInfo();
      case 'SFTP':
        return this.renderSftpUploadInfo();
      default:
        return this.renderSftpUploadInfo();
    }
  };

  renderUploadInfoAndSettings = () => {
    return (
      <Container align="left" full flush>
        <Row size="xsmall" data-test="last-upload-info-row">
          {this.renderUploadInfo()}
        </Row>
      </Container>
    );
  };

  renderCsvValidationTable = () => {
    const {
      districtStatus: { syncType },
      orgUuid,
    } = this.props;

    if (syncType.toString().includes('Spreadsheet')) {
      return (
        <Row>
          <CsvValidationTableContainer orgUuid={orgUuid} />
        </Row>
      );
    }
  };

  renderBody = () => {
    const { childOrganizations, orgUuid, rosteringPermissions, router } = this.props;
    const { canOnlySeeRosteringDashboard, canOnlySeeReviewScreen } = rosteringPermissions;
    const currentUser = CurrentUser.getUser();

    return (
      <section className="AdminAdminsView">
        {this.renderHeader()}
        {canOnlySeeRosteringDashboard &&
          currentUser.hasFeature('rostering_enforcement_checkbox.web.sept-19') &&
          this.renderRosteringEnforcementCheckbox()}
        {this.renderUploadInfoAndSettings()}
        {this.renderCsvValidationTable()}
        <Row>
          <AdminSisSyncSchoolTable
            orgUuid={orgUuid}
            canOnlySeeReviewScreen={canOnlySeeReviewScreen}
            router={router}
            childOrgs={childOrganizations}
          />
        </Row>
      </section>
    );
  };

  render() {
    return <div className="AdminDistrictSisDashboard">{this.renderBody()}</div>;
  }
}
